if (!customElements.get('psl-ice-addon')) {
  customElements.define('psl-ice-addon', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.visorSelect = this.querySelector('[data-visor-select]');
      this.copy = JSON.parse(this.querySelector('[data-addon-copy]').textContent);
      this.message = this.querySelector('[data-addon-message]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.root = window.Shopify?.routes?.root || '/';
      if (this.visorSelect) {
        this.visorSelect.value = '';
        this.querySelector('[data-choices]').hidden = false;
        this.visorSelect.addEventListener('change', () => this.updateChoice(), {signal:this.abort.signal});
      }
      document.addEventListener('submit', event => this.onSubmit(event), {capture:true, signal:this.abort.signal});
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
    }
    controls() {
      return document.querySelector('psl-order-controls[data-section-id="' + this.dataset.section + '"]');
    }
    resetMessage() {
      if (!this.attempted) { this.message.textContent = ''; this.cartLink.hidden = true; }
    }
    updateChoice() {
      const option = this.visorSelect.selectedOptions[0];
      const active = Boolean(this.visorSelect.value);
      this.querySelector('[data-price-line]').hidden = !active;
      this.querySelector('[data-addon-price]').textContent = active ? option.dataset.price : '';
      this.querySelector('[data-addon-discount-price]').textContent = active ? option.dataset.discountPrice : '';
      const image = this.querySelector('[data-addon-image]');
      if (active && image && option.dataset.image) { image.src = option.dataset.image; image.alt = option.textContent; }
      this.resetMessage();
    }
    getCodes(cart) {
      const codes = new Set();
      for (const code of cart.discount_codes || []) if (code.code) codes.add(code.code);
      const collect = application => {
        if (application?.type === 'discount_code' && application.title) codes.add(application.title);
      };
      for (const application of cart.cart_level_discount_applications || []) collect(application);
      for (const item of cart.items || []) {
        for (const allocation of item.line_level_discount_allocations || []) collect(allocation.discount_application);
        for (const allocation of item.discount_allocations || []) collect(allocation.discount_application);
      }
      return [...codes];
    }
    show(message) { this.message.textContent = message; this.cartLink.hidden = false; }
    visorDiscount(cart, visorId) {
      return (cart.items || []).filter(item => Number(item.variant_id || item.id) === visorId && item.properties?._PSL_upsell === 'ICE iceglasses').reduce((total, item) =>
        total + (item.line_level_discount_allocations || []).filter(allocation => String(allocation.discount_application?.title).toLowerCase() === 'iceglasses').reduce((amount, allocation) => amount + Number(allocation.amount || 0), 0), 0);
    }
    async request(path, payload) {
      const response = await fetch(this.root + path, {
        credentials:'same-origin', headers:{'Content-Type':'application/json','Accept':'application/json'},
        method:payload ? 'POST' : 'GET', ...(payload ? {body:JSON.stringify(payload)} : {cache:'no-store'})
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || this.copy.error);
      return result;
    }
    buildItems(data, visorId) {
      const helmetId = Number(data.get('id'));
      const quantity = Number(data.get('quantity') || 1);
      if (!Number.isSafeInteger(helmetId) || helmetId < 1 || !Number.isSafeInteger(quantity) || quantity < 1) return null;
      if (!Number.isSafeInteger(visorId) || visorId < 1) return null;
      const helmet = {id:helmetId, quantity};
      const properties = {};
      for (const [name, value] of data.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      if (Object.keys(properties).length) helmet.properties = properties;
      if (data.get('selling_plan')) helmet.selling_plan = data.get('selling_plan');
      const items = [helmet];
      if (visorId) items.push({id:visorId, quantity:1, properties:{_PSL_upsell:'ICE iceglasses'}});
      return items;
    }
    createCartLinesUpdateEvent(items) {
      const {CartLinesUpdateEvent} = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action:'add', context:'product',
        lines:items.map(item => ({merchandiseId:String(item.id), quantity:item.quantity})),
        promise:deferred.promise
      }));
      return deferred;
    }
    resolveCartLinesUpdate(deferred, cart) {
      if (!deferred) return;
      const {CartLinesUpdateEvent} = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return;
      if (!cart?.currency) { deferred.reject(new Error('Missing currency in cart response')); return; }
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart)});
    }
    async onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== 'product-form-' + this.dataset.section) return;
      if (!this.visorSelect?.value && !this.attempted && !this.busy) return;
      const controls = this.controls();
      if (controls && controls.confirmed === false) return;
      if (!form.checkValidity()) return;
      const button = form.querySelector('[type="submit"]');
      if (!this.busy && (button?.disabled || button?.getAttribute('aria-disabled') === 'true')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.busy) return;
      if (this.attempted) { this.show(this.copy.review); this.cartLink.focus(); return; }
      const visorId = this.visorSelect?.value ? Number(this.visorSelect.value) : null;
      if (this.visorSelect?.value && (!Number.isSafeInteger(visorId) || visorId < 1)) { this.show(this.copy.error); return; }
      const data = new FormData(form);
      const items = this.buildItems(data, visorId);
      if (!items || items.length < 2) { this.show(this.copy.error); return; }
      const helmetId = items[0].id;
      this.busy = true;
      if (this.visorSelect) this.visorSelect.disabled = true;
      button?.setAttribute('aria-disabled','true');
      button?.classList.add('loading');
      const spinner = button?.querySelector('.loading__spinner, .loading-overlay__spinner');
      spinner?.classList.remove('hidden');
      this.message.textContent = '';
      let linesUpdateDeferred;
      let beforeVisorDiscount = 0;
      try {
        if (visorId) {
          const before = await this.request('cart.js');
          if (this.getCodes(before).some(code => code.toLowerCase() !== 'iceglasses')) {
            this.show(this.copy.conflict); return;
          }
          beforeVisorDiscount = this.visorDiscount(before, visorId);
        }
        this.attempted = true;
        linesUpdateDeferred = this.createCartLinesUpdateEvent(items);
        await this.request('cart/add.js', {items});
        const cart = visorId ? await this.request('cart/update.js', {discount:'iceglasses'}) : await this.request('cart.js');
        this.resolveCartLinesUpdate(linesUpdateDeferred, cart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {source:'psl-ice-addon', productVariantId:String(helmetId), cartData:cart});
        }
        if (visorId) {
          const visors = (cart.items || []).filter(item => Number(item.variant_id || item.id) === visorId && item.properties?._PSL_upsell === 'ICE iceglasses');
          const visor = visors[0];
          const expectedDiscount = Math.floor(Number(visor?.original_price || 0) / 2);
          const addedDiscount = this.visorDiscount(cart, visorId) - beforeVisorDiscount;
          const everyVisorDiscounted = visors.length > 0 && visors.every(item => {
            const unitDiscount = Math.floor(Number(item.original_price || 0) / 2);
            const quantity = Number(item.quantity);
            const allocated = (item.line_level_discount_allocations || []).filter(allocation => String(allocation.discount_application?.title).toLowerCase() === 'iceglasses').reduce((total, allocation) => total + Number(allocation.amount || 0), 0);
            return unitDiscount > 0 && Number.isSafeInteger(quantity) && quantity > 0 && allocated >= unitDiscount * quantity;
          });
          const applied = expectedDiscount > 0 && addedDiscount >= expectedDiscount && everyVisorDiscounted;
          if (!applied) { this.show(this.copy.review); return; }
        }
        this.show(this.copy.added);
        const url = new URL(this.cartLink.href, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        location.assign(url.pathname + url.search);
      } catch (error) {
        linesUpdateDeferred?.reject(error);
        const {CartErrorEvent} = window.StandardEvents || {};
        if (CartErrorEvent) this.dispatchEvent(new CartErrorEvent({error:error.message || this.copy.error, code:'SERVICE_UNAVAILABLE'}));
        this.show(this.attempted ? this.copy.review : this.copy.error);
      } finally {
        this.busy = false;
        if (this.visorSelect) this.visorSelect.disabled = false;
        button?.removeAttribute('aria-disabled');
        button?.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }
  });
}

